import React from 'react';
import styles from './ClientCommon.module.css';
import { FieldConfig } from './types';
import { CLIENT_RISK_STATUS_LABELS, CLIENT_RISK_STATUS_OPTIONS, ClientRiskStatus } from '@/types/client';

interface ClientInfoSectionProps {
    title: string;
    fields: FieldConfig[];
    data: any;
    mode: 'view' | 'edit';
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    errors?: Record<string, string>;
    loading?: boolean;
    gridClass?: string;
    changedFields?: Record<string, boolean>;
    requiredFields?: string[];
    renderPreviousValue?: (fieldName: string) => React.ReactNode;
    renderCustomField?: (field: FieldConfig, mode: 'view' | 'edit') => React.ReactNode;
}

export const ClientInfoSection: React.FC<ClientInfoSectionProps> = ({
    title,
    fields,
    data,
    mode,
    onChange,
    errors = {},
    loading = false,
    gridClass,
    changedFields = {},
    requiredFields = [],
    renderPreviousValue,
    renderCustomField
}) => {
    const activeGridClass = gridClass || styles['client-common-grid'];

    return (
        <div className={styles['client-common-section']}>
            {title && <h3>{title}</h3>}
            <div className={activeGridClass}>
                {fields.map((field) => {
                    const error = errors[field.name];
                    const value = data[field.name];
                    const isChanged = changedFields[field.name];
                    const isChecked = value === true || value === 'true' || value === 1 || value === '1';

                    if (renderCustomField) {
                        const customField = renderCustomField(field, mode);
                        if (customField) return <div key={field.name} className={`${styles['client-common-field']} ${field.gridColumn ? styles['full-width'] : ''}`}>{customField}</div>;
                    }

                    if (field.type === 'checkbox') {
                        // View mode: render like other fields with label above and value below
                        if (mode === 'view') {
                            return (
                                <div key={field.name} className={`${styles['client-common-field']} ${field.gridColumn ? styles['full-width'] : ''}`}>
                                    <label>{field.label}</label>
                                    <div className={`${styles['view-value']} ${isChanged ? styles['changed'] : ''}`}>
                                        {isChecked ? 'Yes' : 'No'}
                                        {isChanged && renderPreviousValue && renderPreviousValue(field.name)}
                                    </div>
                                </div>
                            );
                        }
                        // Edit mode: render checkbox with label
                        return (
                            <div key={field.name} className={`${styles['client-common-field']} ${styles['client-checkbox-field']} ${field.gridColumn ? styles['full-width'] : ''} ${isChanged ? styles['changed-checkbox'] : ''}`}>
                                <label className={styles["client-checkbox-label"]}>
                                    <input
                                        type="checkbox"
                                        name={field.name}
                                        checked={isChecked}
                                        onChange={onChange}
                                        disabled={loading || field.disabled}
                                    />
                                    <span>{field.label}</span>
                                </label>
                                {isChanged && renderPreviousValue && renderPreviousValue(field.name)}
                            </div>
                        );
                    }

                    if (mode === 'view') {
                        return (
                            <div key={field.name} className={`${styles['client-common-field']} ${field.gridColumn ? styles['full-width'] : ''}`}>
                                <label>{field.label}</label>
                                <div className={`${styles['view-value']} ${isChanged ? styles['changed'] : ''}`}>
                                    {field.viewValue ? field.viewValue(data) : (value || '—')}
                                    {isChanged && renderPreviousValue && renderPreviousValue(field.name)}
                                </div>
                            </div>
                        );
                    }

                    // Edit Mode (Non-checkbox)
                    const isRequired = field.required || (requiredFields && requiredFields.includes(field.name));

                    return (
                        <div key={field.name} className={`${styles['client-common-field']} ${field.gridColumn ? styles['full-width'] : ''}`}>
                            <label htmlFor={field.name}>
                                {field.label} {isRequired && <span className={styles['required-asterisk']}>*</span>}
                            </label>

                            {field.type === 'select' ? (
                                <select
                                    id={field.name}
                                    name={field.name}
                                    value={value || ''}
                                    onChange={onChange}
                                    className={`${error ? styles['error'] : ''} ${isChanged ? styles['changed-input'] : ''}`}
                                    disabled={loading || field.disabled}
                                >
                                    <option value="">Select {field.label}</option>
                                    {field.options?.map(opt => {
                                        let label = (opt as any).name || (opt as any).group_name || (opt as any).label || opt.id;
                                        if (field.name === 'client_class' && (opt as any).class_isin) {
                                            label = `${(opt as any).name} - ${(opt as any).class_isin}`;
                                        }
                                        return (
                                            <option key={opt.id} value={opt.id}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : field.type === 'textarea' ? (
                                <textarea
                                    id={field.name}
                                    name={field.name}
                                    value={value || ''}
                                    onChange={onChange}
                                    className={`${error ? styles['error'] : ''} ${isChanged ? styles['changed-input'] : ''}`}
                                    disabled={loading || field.disabled}
                                    rows={3}
                                />
                            ) : (
                                <input
                                    type={field.type === 'money' ? 'number' : field.type}
                                    // Email fields hold a comma-separated recipient list.
                                    // Without `multiple`, the browser's native email validation
                                    // rejects commas and marks the input invalid.
                                    multiple={field.type === 'email'}
                                    id={field.name}
                                    name={field.name}
                                    value={value || ''}
                                    onChange={onChange}
                                    className={`${error ? styles['error'] : ''} ${isChanged ? styles['changed-input'] : ''}`}
                                    disabled={loading || field.disabled}
                                />
                            )}

                            {isChanged && renderPreviousValue && renderPreviousValue(field.name)}
                            {error && <span className={styles['client-field-error']}>{error}</span>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const getClientFieldConfigs = (lookups: {
    schemes: any[];
    categories: any[];
    groups: any[];
    distributors: any[];
    countries?: any[];
    clientClasses?: any[];
}) => {
    const basicInfo: FieldConfig[] = [
        { label: 'Client Code', name: 'client_code', type: 'text', required: true },
        { label: 'First Name', name: 'first_name', type: 'text', required: true },
        { label: 'Middle Name', name: 'middle_name', type: 'text' },
        { label: 'Last Name', name: 'last_name', type: 'text', required: true },
        { label: 'Email', name: 'email', type: 'email' },
        { label: 'Contact Number', name: 'contact_no', type: 'text' },
        { label: 'PAN Card', name: 'pan_card', type: 'text' },
        { label: 'FA Account Number', name: 'fa_account_no', type: 'text', required: true },
        { label: 'Inception Date', name: 'inception_date', type: 'date', required: true, viewValue: (d) => d.inception_date ? new Date(d.inception_date).toLocaleDateString() : '—' },
        { label: 'Agreement Date', name: 'agreement_date', type: 'date', required: true, viewValue: (d) => d.agreement_date ? new Date(d.agreement_date).toLocaleDateString() : '—' },
        { label: 'Maturity Date', name: 'maturity_date', type: 'date', viewValue: (d) => d.maturity_date ? new Date(d.maturity_date).toLocaleDateString() : '—' },
        { label: 'Performance Reporting Date', name: 'performance_reporting_date', type: 'date', viewValue: (d) => d.performance_reporting_date ? new Date(d.performance_reporting_date).toLocaleDateString() : '—' },

        // Address Breakdown
        { label: 'Address', name: 'address', type: 'textarea', gridColumn: '1 / -1' },
        { label: 'City', name: 'city', type: 'text' },
        { label: 'State', name: 'state', type: 'text' },
        {
            label: 'Country',
            name: 'country_id',
            type: 'select',
            options: lookups.countries?.map(c => ({ id: c.id, name: c.name })) || [],
            viewValue: (d) => {
                const id = d.country_id || (typeof d.country === 'object' ? d.country?.id : d.country);
                if (!id) return '—';
                const item = lookups.countries?.find(c => c.id === id);
                return item ? item.name : '—';
            }
        },

        { label: 'Occupation', name: 'occupation', type: 'text' },
        { label: 'Notes', name: 'notes', type: 'textarea', gridColumn: '1 / -1' },

        {
            label: 'Risk Status',
            name: 'risk_status',
            type: 'select',
            options: CLIENT_RISK_STATUS_OPTIONS.map((opt) => ({ id: opt.value, name: opt.label })),
            viewValue: (d) => (d.risk_status && d.risk_status in CLIENT_RISK_STATUS_LABELS
                ? CLIENT_RISK_STATUS_LABELS[d.risk_status as ClientRiskStatus]
                : '—'),
        },

        { label: 'NRI Status', name: 'is_nri', type: 'checkbox' },
        { label: 'Active', name: 'is_active', type: 'checkbox' },
    ];

    const associatedInfo: FieldConfig[] = [
        {
            label: 'Scheme',
            name: 'scheme',
            type: 'select',
            options: lookups.schemes,
            viewValue: (d) => {
                const id = d.scheme;
                if (d.scheme_name) return d.scheme_name;
                if (!id) return '—';
                const item = lookups.schemes.find(s => s.id === id);
                return item ? item.name : '—';
            }
        },
        {
            label: 'Category',
            name: 'category',
            type: 'select',
            options: lookups.categories,
            viewValue: (d) => {
                const id = d.category;
                if (d.category_name) return d.category_name;
                if (!id) return '—';
                const item = lookups.categories.find(c => c.id === id);
                return item ? item.name : '—';
            }
        },
        {
            label: 'Group',
            name: 'group',
            type: 'select',
            options: lookups.groups,
            viewValue: (d) => {
                const id = d.group;
                if (d.group_name) return d.group_name;
                if (!id) return '—';
                const item = lookups.groups.find(g => g.id === id);
                return item ? item.name : '—';
            }
        },
        {
            label: 'Client Class',
            name: 'client_class',
            type: 'select',
            options: lookups.clientClasses,
            viewValue: (d) => {
                const id = d.client_class;
                if (!id) return '—';
                const item = lookups.clientClasses?.find(c => c.id === id);
                if (!item) return '—';
                return item.class_isin ? `${item.name} - ${item.class_isin}` : item.name;
            }
        },
        {
            label: 'Distributor',
            name: 'distributor',
            type: 'select',
            options: lookups.distributors,
            viewValue: (d) => {
                const id = d.distributor;
                if (d.distributor_name) return d.distributor_name;
                if (!id) return '—';
                const item = lookups.distributors.find(dist => dist.id === id);
                return item ? item.name : '—';
            }
        },
        {
            label: 'Distributor RM Name',
            name: 'distributor_rm_name',
            type: 'text',
        },
        {
            label: 'Distributor RM Email',
            name: 'relationship_manager_email',
            type: 'email',
        },
        {
            label: 'Distributor Share',
            name: 'distributor_share',
            type: 'text',
            gridColumn: '1 / -1'
        },
        {
            label: 'Fees to be charged by FA',
            name: 'fa_fees_to_be_charged',
            type: 'checkbox'
        },
        {
            label: 'Tax Applicable',
            name: 'is_tax_applicable',
            type: 'checkbox'
        }
    ];

    const bankingInfo: FieldConfig[] = [
        { label: 'Bank Name', name: 'bank_name', type: 'text' },
        { label: 'Bank Account Number', name: 'bank_acc_no', type: 'text' },
        { label: 'IFSC Code', name: 'bank_ifsc', type: 'text' },
        { label: 'MICR Code', name: 'bank_micr', type: 'text' },
        { label: 'Branch Address', name: 'bank_branch_address', type: 'text', gridColumn: '1 / -1' },
    ];

    const tradingAccountInfo: FieldConfig[] = [
        { label: 'Savings Account No', name: 'savings_account_no', type: 'text' },
        { label: 'PIS Account No', name: 'pis_account_no', type: 'text' },
    ];

    const custodyInfo: FieldConfig[] = [
        { label: 'Custody Code', name: 'custody_code', type: 'text' },
        { label: 'Custody DP (Client Demat DP)', name: 'custody_dp', type: 'text' },
        { label: 'Custody Account ID (Client Account ID)', name: 'custody_account_id', type: 'text' },
        {
            label: 'Mode of Holding',
            name: 'mode_of_holding',
            type: 'select',
            options: [
                { id: 'Joint', name: 'Joint' },
                { id: 'Self', name: 'Self' },
                { id: '', name: 'Not Applicable' }
            ],
            viewValue: (d) => d.mode_of_holding || 'Not Applicable'
        },
    ];

    const nomineeInfo: FieldConfig[] = [
        { label: 'Nominee 1 Name', name: 'nominee_name', type: 'text' },
        { label: 'Nominee 1 Relationship', name: 'nominee_relationship', type: 'text' },
        { label: 'Nominee 2 Name', name: 'nominee_2_name', type: 'text' },
        { label: 'Nominee 2 Relationship', name: 'nominee_2_relationship', type: 'text' },
        { label: 'Guardian Name', name: 'guardian_name', type: 'text' },
    ];

    const jointHolderInfo: FieldConfig[] = [
        { label: 'Joint Holder 1 Name', name: 'joint_holder_1_name', type: 'text' },
        { label: 'Joint Holder 1 PAN', name: 'joint_holder_1_pan', type: 'text' },
        { label: 'Joint Holder 2 Name', name: 'joint_holder_2_name', type: 'text' },
        { label: 'Joint Holder 2 PAN', name: 'joint_holder_2_pan', type: 'text' },
    ];

    // Shown for every client; the UBO name is only filled in for non-individual
    // accounts (LLP / partnership / company / trust).
    const contactPersonInfo: FieldConfig[] = [
        { label: 'Contact Person Name', name: 'contact_person_name', type: 'text' },
        { label: 'Contact Person Email', name: 'contact_person_email', type: 'email' },
        { label: 'Contact Person Phone', name: 'contact_person_contact_no', type: 'text' },
        { label: 'UBO (Ultimate Beneficial Owner) Name', name: 'ubo_name', type: 'text' },
    ];

    const tradingSettings: FieldConfig[] = [
        { label: 'Block Amount', name: 'block_amount', type: 'money', viewValue: (d) => d.block_amount !== undefined && d.block_amount !== null ? `₹${d.block_amount.toLocaleString()}` : '—' },
        { label: 'Cash Threshold Limit', name: 'cash_threshold_limit', type: 'money', viewValue: (d) => d.cash_threshold_limit !== undefined && d.cash_threshold_limit !== null ? `₹${d.cash_threshold_limit.toLocaleString()}` : '—' },
        { label: 'Stop Purchase', name: 'stop_purchase', type: 'checkbox' },
        { label: 'Redemption Only', name: 'redemption_only', type: 'checkbox' },
        { label: 'Clearing Cash Enabled', name: 'clearing_cash_enable', type: 'checkbox' },
        { label: 'Derivative Consent', name: 'is_derivative_consent', type: 'checkbox' },
    ];

    const auditInfo: FieldConfig[] = [
        { label: 'Created', name: 'created_ts', type: 'text', viewValue: (d: any) => d.created_ts ? new Date(d.created_ts).toLocaleString() : '—' },
        { label: 'Last Updated', name: 'updated_ts', type: 'text', viewValue: (d: any) => d.updated_ts ? new Date(d.updated_ts).toLocaleString() : '—' },
    ];

    return { basicInfo, associatedInfo, bankingInfo, tradingAccountInfo, custodyInfo, nomineeInfo, jointHolderInfo, contactPersonInfo, tradingSettings, auditInfo };
};
